/* Build in libraries */

import { XCircleIcon } from '@heroicons/react/24/outline';
import { MapPinIcon } from '@heroicons/react/24/solid';

/* Third party libraries */
import { useQueries } from '@tanstack/react-query';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { getAddressDecode, type IGetAddressDecodeResData } from '@/api/misc';
import AlabamaBasedDevelopersWidget from '@/components/Rover/Widgets/AlabamaBasedDevelopersWidget';
import AvgSalesPriceAffordableWidget from '@/components/Rover/Widgets/AvgSalesPriceAffordableWidget';
import AvgSalesPriceMarketRateWidget from '@/components/Rover/Widgets/AvgSalesPriceMarketRateWidget';
import CityInvestmentWidget from '@/components/Rover/Widgets/CityInvestmentWidget';
import DevelopersInvestmentWidget from '@/components/Rover/Widgets/DevelopersInvestmentWidget';
import DevelopmentPipelineWidget from '@/components/Rover/Widgets/DevelopmentPipelineWidget';
import MinorityDevelopersWidget from '@/components/Rover/Widgets/MinorityDevelopersWidget';
import NeighborhoodImpactWidget from '@/components/Rover/Widgets/NeighborhoodImpactWidget';
import TotalAffdForSaleUnitsWidget from '@/components/Rover/Widgets/TotalAffdForSaleUnitsWidget';
import TotalAffdRentalUnitsWidget from '@/components/Rover/Widgets/TotalAffdRentalUnitsWidget';
import TotalCoBLandSalesWidget from '@/components/Rover/Widgets/TotalCoBLandSalesWidget';
import TotalForSaleUnitsWidget from '@/components/Rover/Widgets/TotalForSaleUnitsWidget';
import TotalMarketRentalUnitsWidget from '@/components/Rover/Widgets/TotalMarketRentalUnitsWidget';
/* Components */
import TotalProjectsWidget from '@/components/Rover/Widgets/TotalProjectsWidget';
import TotalUnitsToBeCreatedWidget from '@/components/Rover/Widgets/TotalUnitsToBeCreatedWidget';
import Card from '@/components/UI/Card/index.tsx';
import PieChart from '@/components/UI/Charts/PieChart';
import GoogleMap from '@/components/UI/GoogleMap';
import { PhoneNumberFormatDisplay } from '@/components/UI/NumberFormatDisplay/PhoneNumberFormatDisplay';
/* Services */
import { transformProjectTypeChartDataSets } from '@/utils';

interface IApplicationDashboardComponentProps {
  data: any;
}

export const BDCDPApprovedDashboard = ({ data }: IApplicationDashboardComponentProps) => {
  const location = useLocation();
  const [selectedProject, setSelectedProject] = useState<any>();

  const applicationsList = data?.data;

  const widgetsData: {
    totalProjects: number;
    developmentPipeline: number;
    totalUnitsToBeCreated: number;
    totalCoBLandSales: number;
    cityInvestment: number;
    developerInvestment: number;
    totalAffdForSaleUnitsProposed: number;
    totalAffdRentalUnitsProposed: number;
    totalForSaleUnitsProposed: number;
    totalMarketRentalUnitsProposed: number;
    avgSalesPriceAffordable: number;
    avgSalesPriceMarketRate: number;
    alabamaBasedDevelopers: number;
    minorityDevelopers: number;
    projectType: { [key: string]: number };
    constructionTypes: { [key: string]: number };
    neighborhoods: any[];
    applicationLocationList: Array<{
      id: string;
      [key: string]: string;
      fullAddress: string;
    }>;
  } = {
    totalProjects: 0,
    developmentPipeline: 0,
    totalUnitsToBeCreated: 0,
    totalCoBLandSales: 0,
    cityInvestment: 0,
    developerInvestment: 0,
    totalAffdForSaleUnitsProposed: 0,
    totalAffdRentalUnitsProposed: 0,
    totalForSaleUnitsProposed: 0,
    totalMarketRentalUnitsProposed: 0,
    avgSalesPriceAffordable: 0,
    avgSalesPriceMarketRate: 0,
    alabamaBasedDevelopers: 0,
    minorityDevelopers: 0,
    projectType: {},
    constructionTypes: {},
    neighborhoods: [],
    applicationLocationList: [],
  };
  applicationsList?.forEach((a: any) => {
    const { applicationData, id } = a;
    if (a.status === 'APPROVED') {
      // Location
      const applicationLocation = `${applicationData?.projectDetails?.streetAddress}, ${applicationData?.projectDetails?.city}, ${applicationData?.projectDetails?.state}, ${applicationData?.projectDetails?.zipCode}`;
      widgetsData.applicationLocationList.push({
        id: id,
        email: applicationData?.applicantInfo?.contactPersonEmail,
        name: applicationData?.applicantInfo?.entityName,
        phone: applicationData?.applicantInfo?.phone,
        fullAddress: applicationLocation || '',
      });

      // Total Projects
      widgetsData.totalProjects += 1;

      // Development Pipeline
      applicationData?.projectFinance?.sources.forEach((s: any) => {
        widgetsData.developmentPipeline += s?.amount;

        // City Investment
        if (s?.name === 'City of Birmingham') {
          widgetsData.cityInvestment += s?.amount;
        }

        // Developer Investment
        if (s?.name === 'Developer Equity') {
          widgetsData.developerInvestment += s?.amount;
        }
      });

      // Minority Developer
      if (applicationData?.leadershipInformation?.hasMajorityOwnership === 'yes') {
        widgetsData.minorityDevelopers += 1;
      }

      // Total CoB Land Sales
      if (applicationData?.projectDetails?.isPropertyCityOwn === 'yes') {
        widgetsData.totalCoBLandSales += applicationData?.projectDetails?.proposedAmount;
      }

      // Total Units To Be Created
      widgetsData.totalUnitsToBeCreated += applicationData?.projectDetails?.proposedUnits;

      if (applicationData?.projectDetails?.projectType === 'Affordable Homeownership') {
        widgetsData.totalAffdForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;

        widgetsData.avgSalesPriceAffordable += applicationData?.projectDetails?.avgSalesPrice;
      }

      if (applicationData?.projectDetails?.projectType === 'Affordable Rental Housing: 4%, 9%') {
        widgetsData.totalAffdRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;
      }

      if (applicationData?.projectDetails?.projectType === 'Home Ownership') {
        widgetsData.totalForSaleUnitsProposed += applicationData?.projectDetails?.proposedUnits;
      }

      if (applicationData?.projectDetails?.projectType === 'Market Rate Multifamily') {
        widgetsData.totalMarketRentalUnitsProposed += applicationData?.projectDetails?.proposedUnits;

        widgetsData.avgSalesPriceAffordable += applicationData?.projectDetails?.avgSalesPrice;
      }

      // Alabama Based Developers
      if (applicationData?.applicantInfo?.state === 'Alabama') {
        widgetsData.alabamaBasedDevelopers += 1;
      }

      //Proposed Project Type Summary
      const { projectType } = applicationData.projectDetails;
      widgetsData.projectType[projectType] = (widgetsData.projectType[projectType] ?? 0) + 1;

      //Proposed Construction Type Summary
      const { constructionType } = applicationData.projectDetails;
      widgetsData.constructionTypes[constructionType] = (widgetsData.constructionTypes[constructionType] ?? 0) + 1;

      const { neighborhood, developmentCostEstimate } = applicationData.projectDetails;

      if (neighborhood) {
        const dataIndex = widgetsData.neighborhoods?.findIndex((d) => d?.neighborhood === neighborhood);

        if (dataIndex > -1) {
          widgetsData.neighborhoods[dataIndex]._count += 1;
          widgetsData.neighborhoods[dataIndex].projectsValues += developmentCostEstimate;
        } else {
          widgetsData.neighborhoods.push({
            neighborhood: neighborhood,
            _count: 1,
            projectsValues: developmentCostEstimate,
          });
        }
      }
    }
  });

  const transformDataForPieChart = (data: { [key: string]: number }) => {
    const transformedData = Object.keys(data).map((d) => ({
      type: d,
      count: data[d],
    }));

    return transformProjectTypeChartDataSets(transformedData);
  };

  const fetchApplicationLocation = useQueries({
    queries: widgetsData?.applicationLocationList?.map((d: { id: string; [key: string]: string; fullAddress: string }) => {
      return {
        queryKey: ['GET_APPROVED_APPLICATION_WIDGET_GEO_CODE', d?.id],
        queryFn: () => getAddressDecode(d.fullAddress),
        select: (resData: { data: IGetAddressDecodeResData }) => {
          const { lat, lng } = resData.data;
          return { ...d, coordinates: { lat, lng } };
        },
      };
    }),
  });
  const applicationsGeoLocated = fetchApplicationLocation?.map((d) => d.data);

  return (
    <>
      <section className='my-4'>
        <Card title='Project Location'>
          {applicationsGeoLocated.length === 0 && (
            <div className='w-full flex justify-center items-center h-screen/2'>
              <div className='flex justify-center items-center gap-2 text-primaryColor-normal'>
                <MapPinIcon className='h-8 w-8' />
                <h1>No Projects</h1>
              </div>
              <p>There are no assigned projects to display.</p>
            </div>
          )}
          {applicationsGeoLocated.length > 0 && (
            <div className='w-full grid grid-cols-6 h-screen/2'>
              <div className='col-span-6 w-full border-l p-5'>
                {applicationsGeoLocated[0] && (
                  <GoogleMap
                    defaultCenter={{
                      lat: applicationsGeoLocated[0].coordinates.lat,
                      lng: applicationsGeoLocated[0].coordinates.lng,
                    }}
                  >
                    {applicationsGeoLocated?.map((res: any) => {
                      return (
                        <AdvancedMarker
                          key={res?.id}
                          position={{
                            lat: res?.coordinates.lat,
                            lng: res?.coordinates.lng,
                          }}
                        >
                          <div className='relative'>
                            {selectedProject && selectedProject?.id === res?.id && (
                              <div
                                onClick={() => setSelectedProject(undefined)}
                                className='bg-white border rounded absolute p-3 bottom-4 left-5 w-56'
                              >
                                <XCircleIcon className='h-4 w-4 absolute right-1 top-1 text-neutralColor-normal hover:text-red-500' />
                                <Link
                                  to={`${location.pathname.replace('dashboard', 'applications')}/${
                                    selectedProject?.id
                                  }/application_details`}
                                >
                                  <p className='font-semibold text-primaryColor-normal underline'>{selectedProject?.name}</p>
                                </Link>
                                <p className='text-xs'>{selectedProject?.fullAddress}</p>
                                <div className='mt-2 text-sm flex justify-between gap-2'>
                                  <span>Email</span>
                                  <span className='font-semibold'>{selectedProject?.email}</span>
                                </div>
                                <div className='mt-2 text-sm flex justify-between gap-2'>
                                  <span>Phone</span>
                                  <span className='font-semibold'>
                                    <PhoneNumberFormatDisplay value={selectedProject?.phone || 'none'} />
                                  </span>
                                </div>
                              </div>
                            )}
                            {res && (
                              <MapPinIcon
                                onClick={() => setSelectedProject(res)}
                                className={selectedProject?.id === res?.id ? 'h-8 w-8 text-black' : 'h-8 w-8 text-red-500 '}
                              />
                            )}
                          </div>
                        </AdvancedMarker>
                      );
                    })}
                  </GoogleMap>
                )}
              </div>
            </div>
          )}
        </Card>
      </section>

      <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4'>
        <TotalProjectsWidget count={widgetsData?.totalProjects ?? 0} />
        <DevelopmentPipelineWidget count={widgetsData?.developmentPipeline ?? 0} />
        <TotalCoBLandSalesWidget count={widgetsData?.totalCoBLandSales ?? 0} />
        <TotalUnitsToBeCreatedWidget count={widgetsData?.totalUnitsToBeCreated} />
        <TotalForSaleUnitsWidget count={widgetsData?.totalForSaleUnitsProposed} />
        <TotalAffdForSaleUnitsWidget count={widgetsData?.totalAffdForSaleUnitsProposed} />
        <TotalAffdRentalUnitsWidget count={widgetsData?.totalAffdRentalUnitsProposed} />
        <TotalMarketRentalUnitsWidget count={widgetsData?.totalMarketRentalUnitsProposed} />
        <AvgSalesPriceAffordableWidget count={widgetsData?.avgSalesPriceAffordable} />
        <AvgSalesPriceMarketRateWidget count={widgetsData?.avgSalesPriceMarketRate} />
        <CityInvestmentWidget count={widgetsData?.cityInvestment} />
        <DevelopersInvestmentWidget count={widgetsData?.developerInvestment} />
        <AlabamaBasedDevelopersWidget count={widgetsData?.alabamaBasedDevelopers} />
        <MinorityDevelopersWidget count={widgetsData?.totalProjects ?? 0} />
      </section>
      <section className='grid md:grid-cols-2 xl:grid-cols-4 gap-4 my-10'>
        <div className='col-span-2'>
          <Card title='Projects by Construction Type'>
            <div className='relative p-5 h-80'>
              <div className='absolute px-5 text-center'>
                <h3 className='text-gray-500 font-bold'>Total Construction Types</h3>
                <h1 className='m-0'>{}</h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full'>
                <PieChart
                  labels={Object.keys(widgetsData.constructionTypes)}
                  datasets={transformDataForPieChart(widgetsData.constructionTypes)}
                  chartTitle=''
                />
              </div>
            </div>
          </Card>
        </div>
        <div className='col-span-2'>
          <Card title='Projects by Project Type'>
            <div className='relative p-5 h-80'>
              <div className='absolute px-5 text-center'>
                <h3 className='text-gray-500 font-bold'> Total Project Types</h3>
                <h1 className='m-0'>{}</h1>
              </div>
              <div className='md:w-7/8 xl:w-full h-full'>
                <PieChart
                  labels={Object.keys(widgetsData.projectType)}
                  datasets={transformDataForPieChart(widgetsData.projectType)}
                  chartTitle=''
                />
              </div>
            </div>
          </Card>
        </div>
        <div className='col-span-2'>
          <NeighborhoodImpactWidget data={widgetsData.neighborhoods} />
        </div>
      </section>
    </>
  );
};
